import React, { useState } from 'react';
import {
  Button,
  Caption,
  CaptionStyle,
  CheckCircleIcon,
  Subtitle,
  TinyTitleRegular,
  mq
} from '@kvdbil/components';
import styled from 'styled-components';
import { Color, Size, Variant } from '@kvdbil/components/types/Types';
import { LeasingPartnerPresentationFragment } from '~/config/generated/graphql';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import Link from '~/App/shared/components/Link';
import { slugify } from '~/helpers/slugify';
import {
  LeasingInterestCopy,
  LeasingPartnerModal
} from './LeasingPartnerModal';
import LeasingPromotionCard from './LeasingPromotionCard';
import PartnerLogo from './PartnerLogo';

const BLOCK_SPACING = '2.5rem';

const LOGO_HEIGHT = '3rem';
const COLUMN_WIDTH = '21.75rem';
const WAVE_HEIGHT = '3.75rem';
const WAVE_WIDTH = '48rem';

const Wrapper = styled.div`
  margin: ${BLOCK_SPACING} 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PromotionSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PromotionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 1rem;
  row-gap: 1.5rem;

  ${mq('tablet')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PromotionButtonLink = styled(Link)`
  align-self: center;
  width: 100%;
  max-width: ${COLUMN_WIDTH};
`;

const Panel = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.background.sand};
  color: ${({ theme }) => theme.colors.text.dark};

  ${mq('tablet')} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
  }
`;

const Waves = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: ${WAVE_HEIGHT};
  pointer-events: none;

  img {
    position: absolute;
    bottom: 0;
    width: ${WAVE_WIDTH};
    max-width: none;
    height: 100%;
  }
`;

const WaveBack = styled.img`
  right: 0;
`;

const WaveFront = styled.img`
  left: 50%;
  transform: translateX(-50%);
`;

const Column = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${mq('tablet')} {
    flex: 1 1 ${COLUMN_WIDTH};
    max-width: ${COLUMN_WIDTH};
  }
`;

const DescriptionColumn = styled(Column)`
  gap: 1rem;
`;

const UspColumn = styled(Column)`
  gap: 0.5rem;
`;

const Logo = styled(PartnerLogo)`
  align-self: center;
  height: ${LOGO_HEIGHT};

  ${mq('tablet')} {
    align-self: flex-start;
  }
`;

const DescriptionWrapper = styled.div`
  p {
    ${CaptionStyle};
    margin: 0;
  }

  p + p {
    margin-top: 0;
  }

  p:last-child {
    margin-top: 1.5rem;
  }

  a {
    font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
    text-decoration: none;
  }

  strong,
  b {
    font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  }
`;

const UspList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const UspItem = styled.li`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const UspIcon = styled(CheckCircleIcon)`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary.dark};
`;

const ButtonLink = styled(Link)`
  align-self: stretch;
  max-width: ${COLUMN_WIDTH};
  margin-top: 0.5rem;
`;

const cmsButtonProps = (
  button: NonNullable<LeasingPartnerPresentationFragment['linkButton']>
) => ({
  color: (button.color as Color) ?? 'primary',
  size: (button.size as Size) ?? 'regular',
  variant: (button.variant as Variant) ?? 'solid'
});

type Props = Pick<
  LeasingPartnerPresentationFragment,
  | 'anchorId'
  | 'partnerName'
  | 'leasingProvider'
  | 'logo'
  | 'description'
  | 'uspHeading'
  | 'uspList'
  | 'linkButton'
  | 'promotionHeading'
  | 'promotionVehicleCards'
  | 'promotionButton'
> &
  LeasingInterestCopy;

const LeasingPartnerPresentation = ({
  anchorId,
  partnerName,
  leasingProvider,
  logo,
  description,
  uspHeading,
  uspList,
  linkButton,
  promotionHeading,
  promotionVehicleCards,
  promotionButton,
  ...interestCopy
}: Props) => {
  const usps = uspList.filter(usp => usp.text);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const promotions = promotionVehicleCards.filter(
    card => card.monthlyCostFrom != null
  );

  const selectedCard = promotions.find(card => card.id === selectedCardId);

  return (
    <Wrapper id={anchorId ? slugify(anchorId) : undefined}>
      <Panel>
        <Waves aria-hidden="true">
          <WaveBack
            src="/images/graphics/leasing-partner-wave-back.svg"
            alt=""
          />
          <WaveFront
            src="/images/graphics/leasing-partner-wave-front.svg"
            alt=""
          />
        </Waves>

        <DescriptionColumn>
          <Logo logo={logo} fallbackAlt={partnerName} />

          {description && (
            <StructuredText data={description} wrapper={DescriptionWrapper} />
          )}
        </DescriptionColumn>

        <UspColumn>
          {uspHeading && (
            <TinyTitleRegular as="h3">{uspHeading}</TinyTitleRegular>
          )}

          {usps.length > 0 && (
            <UspList>
              {usps.map(({ id, text }) => (
                <UspItem key={id}>
                  <UspIcon aria-hidden="true" />
                  <Caption as="span">{text}</Caption>
                </UspItem>
              ))}
            </UspList>
          )}

          {linkButton?.url && (
            <ButtonLink
              to={linkButton.url}
              target={linkButton.openLinkInNewWindow ? '_blank' : undefined}
            >
              <Button
                as="span"
                fullWidth
                colorShade="dark"
                {...cmsButtonProps(linkButton)}
              >
                {linkButton.text}
              </Button>
            </ButtonLink>
          )}
        </UspColumn>
      </Panel>

      {promotions.length > 0 && (
        <PromotionSection>
          {promotionHeading && <Subtitle as="h3">{promotionHeading}</Subtitle>}

          <PromotionGrid>
            {promotions.map(card => (
              <LeasingPromotionCard
                key={card.id}
                {...card}
                logo={logo}
                onClick={setSelectedCardId}
              />
            ))}
          </PromotionGrid>

          {promotionButton?.url && (
            <PromotionButtonLink
              to={promotionButton.url}
              target={
                promotionButton.openLinkInNewWindow ? '_blank' : undefined
              }
            >
              <Button
                as="span"
                fullWidth
                colorShade="dark"
                {...cmsButtonProps(promotionButton)}
              >
                {promotionButton.text}
              </Button>
            </PromotionButtonLink>
          )}
        </PromotionSection>
      )}
      {selectedCard && (
        <LeasingPartnerModal
          isOpen
          onClose={() => setSelectedCardId(null)}
          selectedCard={selectedCard}
          leasingProvider={leasingProvider}
          copy={interestCopy}
        />
      )}
    </Wrapper>
  );
};

export default LeasingPartnerPresentation;
